/**
 * Who is signed in on this device, read from the session supabase-js keeps in localStorage,
 * without loading the online code (net/online.ts): Home shows a guest's Register and Log in, or a
 * registered player's league, from its first frame instead of adding them once that code has
 * loaded. loadAccount() then asks supabase-js itself.
 */
import type { Account } from './account';
import { SUPABASE_URL, serverProgress } from './config';

/** The parts of a Supabase user an Account is made from. */
interface SessionUser {
  email?: string | null;
  is_anonymous?: boolean;
  app_metadata?: { providers?: string[] };
}

export function accountOf(user: SessionUser | null | undefined): Account {
  const guest = !user || user.is_anonymous === true;
  // A guest who linked Google or Facebook started as 'anonymous': look at every linked provider.
  const linked = user?.app_metadata?.providers ?? [];
  const provider = guest
    ? null
    : (['facebook', 'google'].find((x) => linked.includes(x)) ?? 'email');
  return { email: user?.email || null, guest, signedIn: !!user, provider };
}

/** Null when it can't be told (no Supabase settings, or no storage). */
export function savedAccount(): Account | null {
  if (!serverProgress() || !SUPABASE_URL) return null;
  try {
    // supabase-js's own key: net/online.ts creates its client without a storageKey.
    const raw = localStorage.getItem(
      `sb-${new URL(SUPABASE_URL).hostname.split('.')[0]}-auth-token`,
    );
    return accountOf(raw ? (JSON.parse(raw) as { user?: SessionUser } | null)?.user : null);
  } catch {
    return null;
  }
}
